import React from 'react'

import { Box, ModalBody, ModalClose, ModalFooter, ModalHeader } from '@gousto-internal/citrus-react'
import { useSelector } from 'react-redux'

import type { RecipeQueryResponse } from '@library/api-menu-experience'

import type { RecipeCoreId, RecipeUUID } from 'reducers/menu-recipes/recipes'
import { AddRecipeButton } from 'routes/Menu/components/Detail/AddRecipeButton'
import { RecipeDescription } from 'routes/Menu/components/Detail/Description'
import { DetailError } from 'routes/Menu/components/Detail/DetailError'
import { RecipeImage } from 'routes/Menu/components/Detail/Image'
import { SectionPanel } from 'routes/Menu/components/Detail/SectionPanel'
import { Title } from 'routes/Menu/components/Detail/Title'
import { getNumPortions } from 'selectors/basket'

import { RecipeAllergens } from './Allergens/RecipeAllergens'
import { RecipeAttributes } from './Attributes/RecipeAttributes'
import { RecipeQuickStats } from './Attributes/RecipeQuickStats'
import { RecipeEquipment } from './Equipment/RecipeEquipment'
import { RecipeIngredients } from './Ingredients/RecipeIngredients'
import { RecipeNutrition } from './Nutrition/RecipeNutrition'

type DetailContentProps = {
  uuid: RecipeUUID
  coreId: RecipeCoreId
  title: string
  error: boolean
  loading: boolean
  recipe: RecipeQueryResponse | null
  position?: string
  reload: () => void
  onClose: () => void
  isScrollableBasketEnabled: boolean
}

export const DetailContent = ({
  uuid,
  coreId,
  title,
  error,
  loading,
  recipe,
  reload,
  onClose,
  position,
  isScrollableBasketEnabled,
}: DetailContentProps) => {
  const numPortions = useSelector(getNumPortions)

  return (
    <>
      <ModalClose onClose={onClose} />
      <ModalHeader>
        <Title data-testing="recipeDetailTitle">{title}</Title>
      </ModalHeader>
      <ModalBody>
        <Box>
          <RecipeImage images={{ experience: recipe?.images }} title={title} />
          <SectionPanel>
            <RecipeQuickStats tagGroups={recipe?.tagGroups} />
            <RecipeDescription
              description={recipe?.description}
              packagingDescriptionHTML={recipe?.packagingDescriptionHTML}
              loading={loading}
            />
            <RecipeAttributes tagGroups={recipe?.tagGroups} loading={loading} />
          </SectionPanel>
          {error ? (
            <DetailError reload={reload} />
          ) : (
            <>
              <RecipeIngredients ingredients={recipe?.ingredients} loading={loading} />
              <RecipeEquipment equipment={recipe?.equipment} loading={loading} onClose={onClose} />
              <RecipeNutrition
                nutrition={recipe?.nutrition}
                numPortions={numPortions}
                tagGroups={recipe?.tagGroups}
                loading={loading}
              />
              <RecipeAllergens
                ingredients={recipe?.ingredients}
                allergens={recipe?.allergens}
                loading={loading}
              />
            </>
          )}
        </Box>
      </ModalBody>
      {!error && (
        <ModalFooter>
          <AddRecipeButton
            coreId={coreId}
            id={uuid}
            surchargeLabel={recipe?.pricing?.perPortionSurcharge}
            isNegativeSurcharge={Boolean(recipe?.pricing?.isNegative)}
            position={position}
            isScrollableBasketEnabled={isScrollableBasketEnabled}
          />
        </ModalFooter>
      )}
    </>
  )
}
